import React from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../Card/Card';
import styles from './RPSHero.module.css';

/**
 * RPSHero — carte signature noire avec score RPS™.
 *
 * Props :
 *  - score      : 76     (0–100)
 *  - delta      : nombre signé (+5, -3, 0) ou null si pas d'historique 30j
 *  - subtitle   : "Bon niveau de réputation"
 *  - target     : 76     (% de remplissage de la barre, défaut = score)
 *
 * Affichage du delta (Sprint 2 P1 — vrai delta calculé backend) :
 *  - delta > 0  → "▲ +X pts" (vert)
 *  - delta < 0  → "▼ −X pts" (rouge, signe minus typographique)
 *  - delta = 0  → "→ stable" (neutre)
 *  - delta null → "Pas encore d'historique" italique gris (resto < 30j ou tout
 *                 premier chargement post-déploiement)
 *
 * IDs préservés pour le backend Express :
 *  - #kpi-rps         (la carte entière)
 *  - #rps-sub-text    (la ligne sous le score)
 */
function buildDeltaPart(delta, t) {
  // Discrimine null/undefined/NaN du nombre 0 légitime.
  if (typeof delta !== 'number' || !Number.isFinite(delta)) {
    return { variant: 'empty', text: t('rps_hero.delta_empty') };
  }
  if (delta > 0) {
    // Triangle haut + chiffre positif. "▲ +5 pts"
    return { variant: 'up', text: t('rps_hero.delta_up', { value: delta }) };
  }
  if (delta < 0) {
    // Triangle bas + signe minus typographique propre (U+2212).
    // Math.abs pour éviter le double signe "▼ -3" laid → "▼ −3".
    return { variant: 'down', text: t('rps_hero.delta_down', { value: Math.abs(delta) }) };
  }
  // delta === 0 : stable.
  return { variant: 'stable', text: t('rps_hero.delta_stable') };
}

export default function RPSHero({
  score = 0,
  delta = null,
  subtitle,
  target,
}) {
  const { t } = useTranslation('dashboard');
  const fillWidth = `${target ?? score}%`;
  const deltaPart = buildDeltaPart(delta, t);
  const safeSubtitle = subtitle || t('rps_hero.default_subtitle');
  const deltaClass =
    deltaPart.variant === 'up' ? styles.deltaUp
    : deltaPart.variant === 'down' ? styles.deltaDown
    : deltaPart.variant === 'stable' ? styles.deltaStable
    : styles.deltaEmpty;

  return (
    <Card span={6} rowSpan={2} id="kpi-rps" className={styles.hero}>
      <div className={styles.tag}>{t('rps_hero.tag')}</div>
      <div className={styles.num} id="kpi-rps-value">
        {score}
        <span>/100</span>
      </div>
      <div className={styles.track}>
        <div
          id="rps-bar"
          className={styles.fill}
          style={{ width: fillWidth }}
        />
      </div>
      <div id="rps-sub-text" className={styles.sub}>
        {deltaPart.variant === 'empty' ? (
          <>
            <em className={deltaClass}>{deltaPart.text}</em> &middot; {safeSubtitle}
          </>
        ) : (
          <>
            <strong className={deltaClass}>{deltaPart.text}</strong> {t('rps_hero.delta_period')} &middot; {safeSubtitle}
          </>
        )}
      </div>
    </Card>
  );
}
